import { useEffect, useState, useCallback } from 'react';
import { getLocalSettingsProxy } from 'utilities';
import {
  PATH_FOR_NETWORK_CHECK,
  NETWORK_STATUS_CHECK_INTERVAL_MILLISECONDS,
} from 'constants';

export default function useNetworkStatus(overrideUrl = null) {
  const [localSettings] = useState(getLocalSettingsProxy);
  const [hasConnection, setHasConnection] = useState(navigator.onLine);
  const isOnline = hasConnection && !localSettings.isUsingForcedOfflineMode;
  const isOffline = !isOnline;

  // Simple request to check if online or not
  const checkConnection = useCallback(() => {
    const fetchURL =
      overrideUrl ??
      `${window.location.protocol}//${window.location.host}${PATH_FOR_NETWORK_CHECK}`;

    // Prevent a cached version from returning a false positive.
    const headers = new Headers();
    headers.append('pragma', 'no-cache');
    headers.append('cache-control', 'no-cache');

    fetch(fetchURL, {
      method: 'HEAD',
      headers,
    })
      .then((res) => {
        const nextHasConnection = res?.status === 200;
        setHasConnection(nextHasConnection);
      })
      .catch(() => {
        setHasConnection(false);
      });
  }, [overrideUrl]);

  // Keep checking network connectivity at regular intervals
  useEffect(() => {
    const checkConnectionInterval = setInterval(
      checkConnection,
      NETWORK_STATUS_CHECK_INTERVAL_MILLISECONDS,
    );
    return () => clearInterval(checkConnectionInterval);
  }, [checkConnection]);

  useEffect(() => {
    const handleOffline = () => {
      setHasConnection(false);
    };
    const handleOnline = () => {
      checkConnection();
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkConnection]);

  return { isOnline, isOffline, hasConnection };
}
